<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;

const TEXT = {
  title: 'Theo dõi truy cập',
  empty: 'Hiện chưa có khách nào đang online trên website.',
  visitorName: 'Tên Khách',
  ip: 'IP',
  pageUrl: 'URL Truy Cập',
  browser: 'Trình Duyệt',
  action: 'Hành động',
  unknownVisitor: 'Khách Vô Danh',
  noTitle: 'Không có tiêu đề',
  notAvailable: 'N/A',
  startChat: 'Bắt chuyện',
  error: 'Có lỗi xảy ra khi tạo cuộc hội thoại',
};

const visitors = ref([]);
const isFetching = ref(true);
const isCreatingConversation = ref(false);
let refreshInterval = null;

const fetchVisitors = async () => {
  try {
    const response = await window.axios.get(
      `/api/v1/accounts/${accountId}/live_visitors`
    );
    visitors.value = response.data;
  } catch (error) {
    useAlert(TEXT.error);
  } finally {
    isFetching.value = false;
  }
};

const startChat = async visitor => {
  if (isCreatingConversation.value) return;
  isCreatingConversation.value = true;

  try {
    const response = await window.axios.post(
      `/api/v1/accounts/${accountId}/live_visitors/${visitor.id}/create_conversation`,
      {
        inbox_id: visitor.inbox_id,
      }
    );
    const conversationId = response.data.id;

    router.push({
      name: 'inbox_conversation',
      params: {
        accountId,
        inbox_id: visitor.inbox_id,
        conversation_id: conversationId,
      },
    });
  } catch (error) {
    useAlert(TEXT.error);
  } finally {
    isCreatingConversation.value = false;
  }
};

onMounted(() => {
  fetchVisitors();
  refreshInterval = setInterval(fetchVisitors, 10000); // Refresh every 10 seconds
});

onUnmounted(() => {
  if (refreshInterval) clearInterval(refreshInterval);
});

const getBrowserInfo = browser => {
  if (!browser) {
    return {
      browserName: 'Unknown',
      browserVersion: '',
      osName: 'Unknown',
    };
  }

  try {
    const parsed = typeof browser === 'string' ? JSON.parse(browser) : browser;
    return {
      browserName: parsed.browser_name || 'Unknown',
      browserVersion: parsed.browser_version || '',
      osName: parsed.os_name || parsed.platform_name || 'Unknown',
    };
  } catch (error) {
    return {
      browserName: String(browser),
      browserVersion: '',
      osName: 'Unknown',
    };
  }
};

const getBrowserName = browser => {
  const browserInfo = getBrowserInfo(browser);
  return browserInfo.browserName === 'Unknown'
    ? 'Unknown'
    : `${browserInfo.browserName} trên ${browserInfo.osName}`;
};

const browserIcon = browserName => {
  const normalizedName = browserName.toLowerCase();
  if (normalizedName.includes('chrome')) return 'i-logos-chrome';
  if (normalizedName.includes('firefox')) return 'i-logos-firefox';
  if (normalizedName.includes('safari')) return 'i-logos-safari';
  if (normalizedName.includes('edge')) return 'i-logos-microsoft-edge';
  if (normalizedName.includes('opera')) return 'i-logos-opera';
  return 'i-lucide-globe';
};

const osIcon = osName => {
  const normalizedName = osName.toLowerCase();
  if (
    normalizedName.includes('mac') ||
    normalizedName.includes('ios') ||
    normalizedName.includes('iphone')
  ) {
    return 'i-logos-apple';
  }
  if (normalizedName.includes('windows')) return 'i-logos-microsoft-windows';
  if (normalizedName.includes('android')) return 'i-logos-android-icon';
  if (normalizedName.includes('linux')) return 'i-logos-linux-tux';
  return 'i-lucide-monitor';
};

const pageTitle = visitor => visitor.page_title || TEXT.noTitle;
const pageUrl = visitor => visitor.page_url || visitor.referer || '';
</script>

<template>
  <div class="flex flex-col flex-1 h-full p-6 m-0 overflow-auto bg-n-surface-1">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-bold text-n-slate-12">
        {{ TEXT.title }}
      </h1>
      <button
        class="p-2 bg-n-slate-3 rounded hover:bg-n-slate-4 flex items-center justify-center"
        @click="fetchVisitors"
      >
        <span class="i-lucide-rotate-cw w-4 h-4 text-n-slate-11" />
      </button>
    </div>

    <div
      v-if="isFetching && visitors.length === 0"
      class="flex items-center justify-center py-10"
    >
      <Spinner />
    </div>

    <div
      v-else-if="visitors.length === 0"
      class="flex flex-col items-center justify-center py-20 text-n-slate-11"
    >
      <span class="i-lucide-users w-12 h-12 mb-4 text-n-slate-8" />
      <p>{{ TEXT.empty }}</p>
    </div>

    <div
      v-else
      class="overflow-x-auto bg-n-background border rounded-lg border-n-slate-4"
    >
      <table class="min-w-full text-left border-collapse">
        <thead>
          <tr
            class="border-b bg-n-slate-2 border-n-slate-4 text-n-slate-11 text-sm"
          >
            <th class="p-4 font-medium">{{ TEXT.visitorName }}</th>
            <th class="p-4 font-medium">{{ TEXT.ip }}</th>
            <th class="p-4 font-medium">{{ TEXT.pageUrl }}</th>
            <th class="p-4 font-medium">{{ TEXT.browser }}</th>
            <th class="p-4 font-medium text-right">{{ TEXT.action }}</th>
          </tr>
        </thead>
        <tbody class="text-sm divide-y divide-n-slate-3">
          <tr
            v-for="visitor in visitors"
            :key="visitor.id"
            class="bg-n-background hover:bg-n-slate-2"
          >
            <td class="p-4 font-medium text-n-slate-12">
              {{ visitor.name || TEXT.unknownVisitor }}
            </td>
            <td class="p-4 text-n-slate-11">
              <div class="flex items-center gap-2">
                <span
                  v-if="visitor.country_code"
                  :class="`flag-icon flag-icon-${visitor.country_code.toLowerCase()}`"
                />
                {{ visitor.ip_address || TEXT.notAvailable }}
              </div>
            </td>
            <td class="p-4 text-n-slate-11 max-w-sm" :title="pageUrl(visitor)">
              <div class="min-w-0">
                <div class="truncate font-medium text-n-slate-12">
                  {{ pageTitle(visitor) }}
                </div>
                <a
                  v-if="pageUrl(visitor)"
                  :href="pageUrl(visitor)"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  class="block truncate text-n-brand"
                >
                  {{ pageUrl(visitor) }}
                </a>
                <span v-else>{{ TEXT.notAvailable }}</span>
              </div>
            </td>
            <td class="p-4 text-n-slate-11">
              <div
                class="flex items-center gap-3"
                :title="getBrowserName(visitor.browser)"
              >
                <div class="flex items-center gap-1.5">
                  <span
                    :class="
                      browserIcon(getBrowserInfo(visitor.browser).browserName)
                    "
                    class="w-5 h-5"
                  />
                  <span class="font-medium text-n-slate-12">
                    {{ getBrowserInfo(visitor.browser).browserName }}
                  </span>
                </div>
                <div class="flex items-center gap-1.5 text-n-slate-10">
                  <span
                    :class="osIcon(getBrowserInfo(visitor.browser).osName)"
                    class="w-5 h-5"
                  />
                  <span>{{ getBrowserInfo(visitor.browser).osName }}</span>
                </div>
              </div>
            </td>
            <td class="p-4 text-right">
              <button
                class="px-4 py-2 text-white bg-[#0052cc] font-semibold rounded hover:bg-[#0747a6] disabled:opacity-50 shadow-sm"
                :disabled="isCreatingConversation"
                @click="startChat(visitor)"
              >
                {{ TEXT.startChat }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
